// pages/SubscriptionDashboard.js
// User's subscription management page
// Last updated: November 27, 2025

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../hooks/useSubscription';
import { supabase } from '../lib/supabase';
import logger from '../utils/logger';

const SubscriptionDashboard = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { subscription, planType, isPro, isLoading: subLoading, refresh } = useSubscription();
  
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://www.whydiditdie.com';

  // Fetch payment history
  useEffect(() => {
    if (user?.email) {
      fetchPaymentHistory();
    }
  }, [user?.email]);

  const fetchPaymentHistory = async () => {
    try {
      setHistoryLoading(true);
      
      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('email', user.email)
        .order('created_at', { ascending: false })
        .limit(10);
      
      if (error) {
        logger.error('Error fetching payment history:', error);
      } else {
        setPaymentHistory(data || []);
      }
    } catch (error) {
      logger.error('Error:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      setCancelLoading(true);
      
      const response = await fetch(`${BACKEND_URL}/api/paystack/subscription/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: user.email })
      });

      const data = await response.json();

      if (data.status) {
        alert(`✅ Subscription cancelled. You'll have access until ${formatDate(data.access_until)}`);
        setShowCancelModal(false);
        refresh(); // Refresh subscription status
        fetchPaymentHistory();
      } else {
        alert(`❌ ${data.message || 'Failed to cancel subscription'}`);
      }
    } catch (error) {
      logger.error('Cancel error:', error);
      alert('❌ Error cancelling subscription. Please try again.');
    } finally {
      setCancelLoading(false);
    }
  };

  const handleUpgrade = () => {
    navigate('/pricing');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'cancelled': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'expired': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'inactive': return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  // Calculate days remaining
  const getDaysRemaining = () => {
    if (!subscription?.ends_at) return null;
    const endsAt = new Date(subscription.ends_at);
    const now = new Date();
    const diffTime = endsAt - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  if (authLoading || subLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-900 to-blue-900 flex items-center justify-center text-white">
        <div className="text-center">
          <div className="text-5xl mb-4 animate-bounce">💳</div>
          <div className="text-xl">Loading your subscription...</div>
        </div>
      </div>
    );
  }

  const daysRemaining = getDaysRemaining();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-900 to-blue-900 text-white">
      {/* Navigation */}
      <nav className="bg-black/90 backdrop-blur-md border-b border-white/10 py-3">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-white hover:text-brand-red transition-colors"
          >
            <span className="text-xl">←</span>
            <span className="font-semibold">Back to Dashboard</span>
          </button>
          
          <div className="flex items-center gap-2">
            <span className="text-2xl">🕵🏽</span>
            <span className="text-xl font-bold text-brand-red">WhyDidItDie</span>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-black mb-2">💳 Subscription & Billing</h1>
          <p className="text-gray-400">Manage your WhyDidItDie subscription</p>
        </div>

        {/* Current Plan Card */}
        <div className={`mb-6 p-6 rounded-2xl border-2 ${
          isPro 
            ? 'bg-gradient-to-br from-yellow-500/10 to-orange-500/10 border-yellow-500/30' 
            : 'bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border-blue-500/30'
        }`}>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div>
              <h2 className="text-2xl font-bold mb-1 flex items-center gap-2">
                {isPro ? '⭐ Pro Plan' : '🎯 Free Trial'}
                {subscription?.status === 'cancelled' && (
                  <span className="text-sm bg-yellow-500/20 text-yellow-400 px-2 py-1 rounded-full">
                    Cancels soon
                  </span>
                )}
              </h2>
              <p className="text-gray-400">
                {isPro ? 'Full access to all features' : 'Try before you commit'}
              </p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-black mb-1">
                {isPro 
                  ? formatCurrency(subscription?.amount || 15)
                  : '$0'
                }
              </div>
              <div className="text-sm text-gray-400">
                {isPro 
                  ? subscription?.billing_cycle === 'annual' ? 'per year' : 'per month'
                  : 'for 30 days'
                }
              </div>
            </div>
          </div>

          {/* Plan Details */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white/5 rounded-lg p-4">
              <div className="text-xs text-gray-400 mb-1">Status</div>
              <div className="font-semibold flex items-center gap-2">
                {isPro ? (
                  subscription?.status === 'cancelled' ? (
                    <span className="text-yellow-400">⚠️ Cancelled</span>
                  ) : (
                    <span className="text-green-400">✅ Active</span>
                  )
                ) : (
                  <span className="text-blue-400">🎯 Trial</span>
                )}
              </div>
            </div>
            
            <div className="bg-white/5 rounded-lg p-4">
              <div className="text-xs text-gray-400 mb-1">
                {isPro ? 'Access Until' : 'Trial Period'}
              </div>
              <div className="font-semibold">
                {isPro 
                  ? formatDate(subscription?.ends_at)
                  : '30 days from signup'
                }
              </div>
            </div>
            
            <div className="bg-white/5 rounded-lg p-4">
              <div className="text-xs text-gray-400 mb-1">
                {isPro ? 'Days Remaining' : 'Plan Type'}
              </div>
              <div className="font-semibold">
                {isPro 
                  ? (daysRemaining !== null ? `${daysRemaining} days` : 'N/A')
                  : 'Free Trial'
                }
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-4 flex-wrap">
            {!isPro ? (
              <button
                onClick={handleUpgrade}
                className="flex-1 bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-6 py-3 rounded-lg font-bold hover:from-yellow-600 hover:to-orange-600 transition-all transform hover:scale-105"
              >
                🚀 Upgrade to Pro - $15/mo
              </button>
            ) : (
              <>
                {subscription?.status !== 'cancelled' && (
                  <>
                    <button
                      onClick={handleUpgrade}
                      className="flex-1 bg-white/10 border border-white/20 text-white px-6 py-3 rounded-lg font-semibold hover:bg-white/20 transition-all"
                    >
                      📊 Change Plan
                    </button>
                    <button
                      onClick={() => setShowCancelModal(true)}
                      className="bg-red-500/20 border border-red-500/30 text-red-400 px-6 py-3 rounded-lg font-semibold hover:bg-red-500/30 transition-all"
                    >
                      ❌ Cancel
                    </button>
                  </>
                )}
                {subscription?.status === 'cancelled' && (
                  <button
                    onClick={handleUpgrade}
                    className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500 text-white px-6 py-3 rounded-lg font-bold hover:from-green-600 hover:to-emerald-600 transition-all"
                  >
                    🔄 Resubscribe
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {/* Features List */}
        <div className="mb-6 bg-white/5 rounded-2xl p-6 border border-white/10">
          <h3 className="text-xl font-bold mb-4">
            {isPro ? '✅ Your Pro Features' : '🎯 Trial Features'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              '📊 Campaign Detective + Deep Analysis',
              '🕵🏽 Waste Detective & Budget Forensics',
              '🎯 Platform Intelligence',
              '🧠 Market Intelligence Engine',
              '🤖 AI Content Engine',
              '👑 Decision Tracker'
            ].map((feature, idx) => (
              <div key={idx} className="flex items-center gap-2 text-sm">
                <span className="text-green-400">✓</span>
                <span>{feature}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Payment History */}
        <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
          <h3 className="text-xl font-bold mb-4">💰 Payment History</h3>
          
          {historyLoading ? (
            <div className="text-center py-8 text-gray-400">
              Loading payment history...
            </div>
          ) : paymentHistory.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <div className="text-4xl mb-2">📭</div>
              <p>No payment history yet</p>
              {!isPro && (
                <button
                  onClick={handleUpgrade}
                  className="mt-4 bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-6 py-2 rounded-lg font-semibold hover:from-yellow-600 hover:to-orange-600 transition-all"
                >
                  Make your first payment
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-3 px-2 text-sm text-gray-400">Date</th>
                    <th className="text-left py-3 px-2 text-sm text-gray-400">Plan</th>
                    <th className="text-left py-3 px-2 text-sm text-gray-400">Amount</th>
                    <th className="text-left py-3 px-2 text-sm text-gray-400">Status</th>
                    <th className="text-right py-3 px-2 text-sm text-gray-400">Reference</th>
                  </tr>
                </thead>
                <tbody>
                  {paymentHistory.map((payment, idx) => (
                    <tr key={idx} className="border-b border-white/5 hover:bg-white/5">
                      <td className="py-3 px-2 text-sm">
                        {formatDate(payment.created_at)}
                      </td>
                      <td className="py-3 px-2 text-sm capitalize">
                        {payment.plan} ({payment.billing_cycle})
                      </td>
                      <td className="py-3 px-2 text-sm font-semibold">
                        {formatCurrency(payment.amount)}
                      </td>
                      <td className="py-3 px-2">
                        <span className={`text-xs px-2 py-1 rounded-full border ${getStatusColor(payment.status)}`}>
                          {payment.status}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-right">
                        <span className="text-xs text-gray-500 font-mono">
                          {payment.paystack_reference?.slice(0, 20)}...
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Help Section */}
        <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl text-center">
          <p className="text-sm text-gray-300">
            Need help with billing? Contact us at{' '}
            <a href="mailto:support@whydiditdie.com" className="text-blue-400 hover:underline">
              support@whydiditdie.com
            </a>
          </p>
        </div>
      </div>

      {/* Cancel Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-red-500/30 rounded-2xl p-6 max-w-md w-full">
            <h3 className="text-2xl font-bold mb-4 text-red-400">⚠️ Cancel Subscription?</h3>
            <p className="text-gray-300 mb-4">
              Are you sure you want to cancel? You'll keep access until <strong>{formatDate(subscription?.ends_at)}</strong>, 
              but won't be charged again.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              You can always resubscribe later to continue using Pro features.
            </p>
            <div className="flex gap-4">
              <button
                onClick={() => setShowCancelModal(false)}
                disabled={cancelLoading}
                className="flex-1 bg-white/10 border border-white/20 text-white px-4 py-3 rounded-lg font-semibold hover:bg-white/20 transition-all"
              >
                Keep Subscription
              </button>
              <button
                onClick={handleCancelSubscription}
                disabled={cancelLoading}
                className="flex-1 bg-red-500 text-white px-4 py-3 rounded-lg font-semibold hover:bg-red-600 transition-all disabled:opacity-50"
              >
                {cancelLoading ? 'Cancelling...' : 'Yes, Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionDashboard;